import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X, Zap, AlertCircle, ArrowUpRight } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useViewportAnchoredPanel } from '../../hooks/useViewportAnchoredPanel';
import Portal from './Portal';

/**
 * QuickActionsFAB - Reusable Floating Action Button with Quick Actions dropdown
 * 
 * Features:
 * - Dropdown menu with quick actions
 * - Click-outside detection
 * - Escape key handling with proper cleanup
 * - Focus trap when dropdown is open
 * - Mobile safe area support
 * - Standardized z-index (z-[9998])
 * - Memoized to prevent unnecessary re-renders
 * 
 * @param {Object} props
 * @param {Array} props.actions - Array of quick action objects
 * @param {'employee' | 'admin'} props.theme - Theme for FAB styling (admin theme retained for reuse; AdminLayout no longer mounts this FAB)
 */
const QuickActionsFAB = React.memo(({ actions = [], theme = 'employee' }) => {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const dropdownRef = useRef(null);
  const fabAnchorRef = useRef(null);

  // Click outside to close Quick Actions menu
  // Ref must be attached to container that wraps BOTH dropdown AND FAB button
  const quickActionsRef = useClickOutside(
    () => setShowQuickActions(false),
    showQuickActions // Only active when menu is open
  );

  const { panelRect } = useViewportAnchoredPanel({
    open: showQuickActions,
    anchorRef: fabAnchorRef,
    placement: 'above',
    maxWidth: 320,
    minWidth: 200,
    maxHeightCap: 480,
    minVisibleHeight: 120,
  });

  // Escape key handler with AbortController for proper cleanup
  useEffect(() => {
    if (!showQuickActions) return;

    const controller = new AbortController();
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowQuickActions(false);
      }
    };

    document.addEventListener('keydown', handleEscape, { 
      signal: controller.signal 
    });

    return () => {
      controller.abort();
    };
  }, [showQuickActions]);

  // Focus trap: Focus first focusable element when dropdown opens
  useEffect(() => {
    if (showQuickActions && dropdownRef.current) {
      const firstFocusable = dropdownRef.current.querySelector(
        'a, button, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  }, [showQuickActions]);

  // Determine if there are urgent actions or badges
  const hasUrgentActions = actions.some(action => action.urgent || (action.badge && action.badge > 0));

  // Theme-based styling
  const fabButtonClass = theme === 'admin'
    ? 'bg-gradient-to-r from-red-600 to-red-700 focus-visible:ring-red-500/50'
    : 'bg-gradient-to-r from-blue-700 to-blue-800 focus-visible:ring-blue-500/50';

  const iconButtonClass = theme === 'admin'
    ? 'icon-button-admin'
    : 'icon-button';

  return (
    <Portal>
      <div ref={quickActionsRef} className="fixed bottom-6 right-6 z-[9998] pb-safe pr-safe">
        {/* Quick Actions Dropdown */}
        {showQuickActions && (
          <div
            ref={dropdownRef}
            className="fixed z-[9999] flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl transition-all duration-200"
            role="menu"
            aria-label="Quick Actions"
            style={
              panelRect
                ? {
                    left: panelRect.left,
                    width: panelRect.width,
                    maxHeight: panelRect.maxHeight,
                    ...(panelRect.top != null ? { top: panelRect.top } : {}),
                    ...(panelRect.bottom != null ? { bottom: panelRect.bottom } : {}),
                  }
                : { visibility: 'hidden', pointerEvents: 'none' }
            }
          >
            <div className="mb-4 flex flex-shrink-0 items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <button
                onClick={() => setShowQuickActions(false)}
                className={iconButtonClass}
                aria-label="Close quick actions menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-0.5">
              {actions.map((action, index) => {
                const Icon = action.icon;
                const isExternalAction = !!action.action;
                const Tag = isExternalAction ? 'button' : Link;
                const tagProps = isExternalAction
                  ? {
                      type: 'button',
                      onClick: () => {
                        setShowQuickActions(false);
                        action.action();
                      },
                    }
                  : {
                      to: action.link,
                      onClick: () => setShowQuickActions(false),
                    };

                return (
                  <Tag
                    key={index}
                    {...tagProps}
                    className={`flex items-center p-3 border rounded-xl hover:bg-gray-50 transition-all duration-200 group w-full text-left ${
                      action.urgent 
                        ? 'border-red-200 bg-red-50/30 hover:border-red-300' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    aria-label={`${action.title}. ${action.description}${action.urgent ? '. Urgent action required.' : ''}${action.badge ? ` ${action.badge} items pending.` : ''}`}
                    role="menuitem"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 relative ${
                      action.color === 'blue' ? 'bg-blue-100' :
                      action.color === 'green' ? 'bg-green-100' :
                      action.color === 'purple' ? 'bg-purple-100' :
                      action.color === 'orange' ? 'bg-orange-100' :
                      action.color === 'teal' ? 'bg-teal-100' :
                      action.color === 'red' ? 'bg-red-100' :
                      action.color === 'yellow' ? 'bg-yellow-100' :
                      'bg-gray-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        action.color === 'blue' ? 'text-blue-800' :
                        action.color === 'green' ? 'text-green-600' :
                        action.color === 'purple' ? 'text-purple-600' :
                        action.color === 'orange' ? 'text-orange-600' :
                        action.color === 'teal' ? 'text-teal-600' :
                        action.color === 'red' ? 'text-red-600' :
                        action.color === 'yellow' ? 'text-yellow-600' :
                        'text-gray-600'
                      }`} aria-hidden="true" />
                      {action.badge > 0 && (
                        <span 
                          className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center" 
                          aria-label={`${action.badge} pending items`}
                        >
                          {action.badge > 9 ? '9+' : action.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                          {action.title}
                        </h4>
                        {action.urgent && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" aria-hidden="true" />
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-1">{action.description}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" aria-hidden="true" />
                  </Tag>
                );
              })}
            </div>
          </div>
        )}
        
        {/* FAB Button — anchor for viewport-safe panel placement */}
        <button
          ref={fabAnchorRef}
          type="button"
          onClick={() => setShowQuickActions(!showQuickActions)}
          className={`w-14 h-14 ${fabButtonClass} text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group focus-visible:outline-none focus-visible:ring-4 hover:scale-105 ${
            showQuickActions ? 'rotate-45' : ''
          }`}
          aria-label={showQuickActions ? 'Close quick actions menu' : 'Open quick actions menu'}
          aria-expanded={showQuickActions}
        >
          <Zap className="w-6 h-6" />
        </button>
        
        {/* Badge for urgent actions */}
        {hasUrgentActions && !showQuickActions && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            !
          </div>
        )}
      </div>
    </Portal>
  );
});

QuickActionsFAB.displayName = 'QuickActionsFAB';

export default QuickActionsFAB;

